import React from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { FiAlertTriangle } from 'react-icons/fi';
import { useTranslation, Trans } from 'react-i18next';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useMotionConfig } from '../../hooks/useReducedMotion';
import { useFocusTrap } from '../../hooks/useFocusTrap';

const InsuranceWarningModal = ({ isOpen, onClose, onConfirm, bikeCount, insuranceCount, addonsData }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();
  const motion = useMotionConfig();
  const focusTrapRef = useFocusTrap(isOpen, onClose);

  // Find insurance addon price from addonsData
  const insuranceAddon = addonsData
    ? Object.values(addonsData).find(a => {
        const name = (a.name || '').toLowerCase();
        const category = (a.category || '').toLowerCase();
        return name.includes('insurance') || category.includes('insurance');
      })
    : null;

  const insurancePrice = insuranceAddon ? parseFloat(insuranceAddon.price || 0) : 0;
  const newInsuranceTotal = bikeCount * insurancePrice;

  const fewerInsurance = insuranceCount < bikeCount;

  return (
    <AnimatePresence>
      {isOpen && (
        <m.div
          {...motion.modalOverlay}
          onClick={onClose}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
        >
          <m.div
            ref={focusTrapRef}
            {...motion.modalContent}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full my-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="insurance-warning-title"
          >
            {/* Warning Icon */}
            <div className="flex justify-center pt-6">
              <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                <SafeIcon icon={FiAlertTriangle} className="w-8 h-8 text-yellow-600" />
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pt-4 pb-6 text-center">
              <h3 id="insurance-warning-title" className="text-lg font-bold text-gray-900 mb-3">
                {t('insuranceWarning.title')}
              </h3>

              <p className="text-sm text-gray-700 mb-2">
                {fewerInsurance ? (
                  bikeCount === 1 ? (
                    <Trans
                      i18nKey="insuranceWarning.selectedItemsFewer"
                      values={{ bikeCount, insuranceCount }}
                      components={{ b: <b /> }}
                    />
                  ) : (
                    <Trans
                      i18nKey="insuranceWarning.selectedItemsFewer_plural"
                      values={{ bikeCount, insuranceCount }}
                      components={{ b: <b /> }}
                    />
                  )
                ) : (
                  bikeCount === 1 ? (
                    <Trans
                      i18nKey="insuranceWarning.selectedItemsMore"
                      values={{ bikeCount, insuranceCount }}
                      components={{ b: <b /> }}
                    />
                  ) : (
                    <Trans
                      i18nKey="insuranceWarning.selectedItemsMore_plural"
                      values={{ bikeCount, insuranceCount }}
                      components={{ b: <b /> }}
                    />
                  )
                )}
              </p>

              <p className="text-sm text-gray-700 mb-4">
                <Trans
                  i18nKey="insuranceWarning.notSupportedMessage"
                  components={{ b: <b /> }}
                />
              </p>

              <p className="text-sm text-gray-600 mb-2">
                {fewerInsurance ? (
                  <Trans
                    i18nKey="insuranceWarning.adjustQuestion"
                    values={{ bikeCount }}
                    components={{ b: <b /> }}
                  />
                ) : (
                  <Trans
                    i18nKey="insuranceWarning.adjustQuestionReduce"
                    values={{ bikeCount }}
                    components={{ b: <b /> }}
                  />
                )}
              </p>

              {insurancePrice > 0 && (
                <p className="text-sm font-semibold text-gray-800 mb-4">
                  {t('insuranceWarning.insuranceTotal', { total: formatCurrency(newInsuranceTotal) })}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="px-6 pb-6 flex flex-col gap-3">
              <button
                onClick={onConfirm}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-opacity hover:opacity-90"
                style={{ background: theme.colors.primary.gradient || theme.colors.primary.base }}
              >
                {fewerInsurance
                  ? t('insuranceWarning.insureAllItems', { bikeCount })
                  : t('insuranceWarning.reduceInsurance', { bikeCount })
                }
              </button>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm transition-colors hover:bg-gray-50"
              >
                {t('insuranceWarning.removeInsurance')}
              </button>
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default InsuranceWarningModal;
